import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  notification,
  Card,
  Typography,
  Row,
  Col,
  Tag,
  Space,
  Statistic,
  Spin
} from "antd";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { 
  ShoppingOutlined, 
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  StockOutlined,
  ShoppingCartOutlined,
  DollarOutlined
} from "@ant-design/icons";
import "./Styles.css";

// Custom theme colors for Zambian fintech (copied from home.jsx)
const ZAMBIA_THEME = {
  primary: "#078930", // Zambian green
  secondary: "#EF3340", // Zambian red
  accent: "#FFB81C", // Gold accent
  neutral: "#2D2D2D", // Dark neutral
};

const { Title, Text } = Typography;

const Commodities = () => {
  const token = localStorage.getItem("token");
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingCommodity, setEditingCommodity] = useState(null);
  const { company } = useContext(AppContext);
  
  // Column configuration for commodities table
  const columns = [
    {
      title: "Commodity",
      dataIndex: "commodity",
      render: (text) => (
        <Space>
          <ShoppingCartOutlined style={{ color: ZAMBIA_THEME.primary }} />
          <span style={{ fontWeight: "bold" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: "SKU",
      dataIndex: "sku",
      render: (sku) => (
        <Tag color={ZAMBIA_THEME.neutral}>
          {sku}
        </Tag>
      ),
    },
    {
      title: "Cost Price (ZMW)",
      dataIndex: "cost_price",
      render: (price) => (
        <span style={{ color: ZAMBIA_THEME.primary, fontWeight: "bold" }}>
          {price.toLocaleString('en-ZA', { style: 'currency', currency: 'ZMW' })}
        </span>
      ),
    },
    {
      title: "Quantity in Stock",
      dataIndex: "quantity",
      render: (qty) => (
        <Tag color={qty > 10 ? ZAMBIA_THEME.primary : ZAMBIA_THEME.secondary}>
          {qty} units
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "published" ? ZAMBIA_THEME.primary : ZAMBIA_THEME.secondary}>
          {status === "published" ? "Active" : status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            style={{ background: ZAMBIA_THEME.primary, borderColor: ZAMBIA_THEME.primary }}
          />
          <Button 
            danger
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchCommodities();
  }, [company?.id, token]);

  const fetchCommodities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/commodities`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setCommodities(response.data.data);
    } catch (err) {
      notification.error({
        message: "Error",
        description: "Failed to fetch commodities data. Please check your connection.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setEditingCommodity(null);
    setIsModalVisible(true);
  };

  const handleEdit = (commodity) => {
    form.setFieldsValue({
      commodity: commodity.commodity,
      sku: commodity.sku,
      cost_price: commodity.cost_price,
      quantity: commodity.quantity,
      status: commodity.status,
    });
    setEditingCommodity(commodity);
    setIsModalVisible(true);
  };

  const handleDelete = (commodityId) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete this commodity? This cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axios.delete(
            `https://zedlenders.pickmesms.com/items/commodities/${commodityId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          notification.success({
            message: "Success",
            description: "Commodity deleted successfully!",
          });
          
          fetchCommodities();
        } catch (err) {
          notification.error({
            message: "Error",
            description: err.response?.data?.errors?.[0]?.message || "Failed to delete commodity",
          });
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const url = editingCommodity
        ? `https://zedlenders.pickmesms.com/items/commodities/${editingCommodity.id}`
        : `https://zedlenders.pickmesms.com/items/commodities`;

      const method = editingCommodity ? "patch" : "post";
      
      // Set default status if creating a new commodity
      const payload = editingCommodity 
        ? values 
        : { ...values, status: "published" };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      notification.success({
        message: "Success",
        description: `Commodity ${editingCommodity ? "updated" : "created"} successfully!`,
      });

      setIsModalVisible(false);
      fetchCommodities();
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.errors?.[0]?.message || "Failed to save commodity",
      });
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}>
        <Spin size="large" tip="Loading commodities..." />
      </div>
    );
  }

  const totalStock = commodities.reduce((total, item) => total + item.quantity, 0);
  const stockValue = commodities.reduce((total, item) => total + (item.cost_price * item.quantity), 0);
  const uniqueItems = commodities.length;

  return (
    <div className="dashboard-container">
      <Card
        className="welcome-banner"
        style={{
          background: `linear-gradient(135deg, ${ZAMBIA_THEME.primary}40, ${ZAMBIA_THEME.accent}30)`,
          marginBottom: 24,
          borderRadius: 8,
          border: `1px solid ${ZAMBIA_THEME.primary}20`,
        }}
      >
        <Row align="middle" gutter={[24, 0]}>
          <Col>
            <ShoppingOutlined style={{ fontSize: 36, color: ZAMBIA_THEME.primary }} />
          </Col>
          <Col>
            <Title level={3} style={{ margin: 0, color: ZAMBIA_THEME.neutral }}>
              Commodity Management
            </Title>
            <Text>Manage home essentials commodities for loans</Text>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]} className="metric-row">
        <Col xs={24} sm={12} md={8}>
          <Card className="metric-card" bordered={false}>
            <Statistic
              title="Total Commodities"
              value={uniqueItems}
              valueStyle={{ color: ZAMBIA_THEME.primary, fontSize: 24 }}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card className="metric-card" bordered={false}>
            <Statistic
              title="Total Items in Stock"
              value={totalStock}
              valueStyle={{ color: ZAMBIA_THEME.primary, fontSize: 24 }}
              prefix={<StockOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card className="metric-card" bordered={false}>
            <Statistic
              title="Total Stock Value"
              value={stockValue}
              valueStyle={{ color: ZAMBIA_THEME.primary, fontSize: 24 }}
              prefix={<DollarOutlined />}
              suffix="ZMW"
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <ShoppingCartOutlined style={{ color: ZAMBIA_THEME.primary }} />
            <span>Home Essentials Inventory</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{ background: ZAMBIA_THEME.primary, borderColor: ZAMBIA_THEME.primary }}
          >
            Add New Commodity
          </Button>
        }
        className="commodity-card"
        bordered={false}
      >
        <Table
          columns={columns}
          dataSource={commodities}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <ShoppingCartOutlined style={{ color: ZAMBIA_THEME.primary }} />
            {editingCommodity ? "Edit Commodity" : "Add New Commodity"}
          </Space>
        }
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Commodity Name"
            name="commodity"
            rules={[{ required: true, message: "Please enter commodity name" }]}
          >
            <Input placeholder="E.g., 25KG Mealie Meal, 1L Cooking Oil, etc." />
          </Form.Item>

          <Form.Item
            label="SKU Code"
            name="sku"
            rules={[{ required: true, message: "Please enter SKU code" }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter SKU code" />
          </Form.Item>

          <Form.Item
            label="Cost Price (ZMW)"
            name="cost_price"
            rules={[{ required: true, message: "Please enter cost price" }]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter cost price"
              formatter={value => `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/ZMW\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            label="Quantity in Stock"
            name="quantity"
            rules={[{ required: true, message: "Please enter quantity" }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter quantity" min={0} />
          </Form.Item>

          {editingCommodity && (
            <Form.Item
              label="Status"
              name="status"
              initialValue="published"
            >
              <Input disabled defaultValue="published" />
            </Form.Item>
          )}

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit"
              style={{ background: ZAMBIA_THEME.primary, borderColor: ZAMBIA_THEME.primary }}
            >
              {editingCommodity ? "Update Commodity" : "Add Commodity"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Commodities;